import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  parseSelectFilterOptions,
  constructQueryParamName,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { filterValidOptions } from '../../util/nameFilterRegex';
import {
  filterValidSeasons,
  yearToSeasonSuffix,
  seasonIncludesYearSuffix,
} from '../../util/seasonFilterRegex';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import {
  fetchAuthors,
  fetchListingIndex,
  fetchListingsByIds,
  fetchKeywordMatches,
  RESULT_PAGE_SIZE,
} from './SearchPage.duck';
import { trackMetaEvent, getUserIdentifiers } from '../../util/metaPixel';

import { H3, H5, NamedRedirect, Page, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
  combineKeywordTokens,
  extractManualKeywordTokens,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import FilterComponentMobile from './FilterComponentMobile';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageTabs from './SearchPageTabs';
import UsersPanel from './UsersPanel';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import SelectMultipleFilterMobile from './SelectMultipleFilterMobile/SelectMultipleFilterMobile';
import FilterBadge from './FilterBadge/FilterBadge';

import css from './SearchPage.module.css';
import Footer from '../../components/Cobe/Footer/Footer';
import { ReactComponent as AdjustmentsIcon } from '../../assets/icons/adjustments-horizontal.svg';
import { ReactComponent as ArrowUturnLeftIcon } from '../../assets/icons/arrow-uturn-left.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Strips accents/diacritics so visually-similar names (e.g. "Pavlović" vs
// "Pavlovic") can be recognized as the same underlying name. Only handles
// characters that Unicode decomposes into a base letter + combining mark
// (e.g. ó -> o + ´) - letters like Danish "ø" or German "ß" are their own
// base codepoint and don't decompose this way, so those are folded
// separately below.
const stripDiacritics = str => str.normalize('NFD').replace(/[̀-ͯ]/g, '');

const SPECIAL_LETTER_FOLD_MAP = {
  ø: 'o', æ: 'ae', œ: 'oe', ß: 'ss', đ: 'd', ł: 'l', ı: 'i', þ: 'th', ð: 'd',
};
const foldSpecialLetters = str =>
  str.replace(/[øæœßđłıþð]/g, ch => SPECIAL_LETTER_FOLD_MAP[ch] || ch);

const foldToBaseLetters = str => foldSpecialLetters(stripDiacritics(str).toLowerCase());

// True if the label uses any accented/special letter at all (either the
// NFD-decomposable kind or one of the standalone letters above) - used to
// prefer the "properly spelled" variant when collapsing near-duplicates.
const hasSpecialLetters = str => foldToBaseLetters(str) !== str.toLowerCase();

const normalizeForDedupeKey = label =>
  foldToBaseLetters(label)
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

// A real person's name should only contain letters (any language/script),
// spaces, hyphens, apostrophes and dots (for initials) - no digits or other
// symbols. Catches jersey numbers/placeholders typed into the free-text
// player field instead of a real name, e.g. "#16 Sander", "(10)", "12 VFB
// und ich". Deliberately Unicode-aware (\p{L}/\p{M}) rather than restricted
// to German umlauts, so international names like "Abdón" or "Sérgio" aren't
// wrongly rejected.
const PLAUSIBLE_NAME_PATTERN = /^[\p{L}\p{M}\s\-'.]+$/u;
const looksLikeARealName = label =>
  typeof label === 'string' && PLAUSIBLE_NAME_PATTERN.test(label.trim());

// Sellers sometimes enter the "same" club/player under slightly different
// spellings (missing umlauts, different diacritics). Collapse those into a
// single entry instead of listing near-duplicates side by side, preferring
// whichever spelling keeps its accented characters (usually the correct one).
const dedupeNearDuplicateLabels = items => {
  const byKey = new Map();
  items.forEach(item => {
    const key = normalizeForDedupeKey(item.label);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      return;
    }
    const itemIsBetter =
      hasSpecialLetters(item.label) !== hasSpecialLetters(existing.label)
        ? hasSpecialLetters(item.label)
        : item.label.length > existing.label.length;
    if (itemIsBetter) {
      byKey.set(key, item);
    }
  });
  return Array.from(byKey.values());
};

// Splits a free-text search token into individual words, e.g. "Luis Suarez"
// -> ["luis", "suarez"]. The 'keywords' URL param is comma-joined between
// distinct filter selections (club/league/...), so a manually typed phrase
// keeps its internal spaces as a single comma-separated segment - without
// this split, a two-word search was compared as one literal substring and
// only matched a listing that contained that exact phrase verbatim.
const splitIntoWords = token =>
  `${token}`
    .split(/\s+/)
    .map(part => part.trim())
    .filter(Boolean);

// Counts how many of the given (already-lowercased) search words appear in
// any of the listing's text fields. Deliberately OR, not AND: a listing
// doesn't need to contain every word to qualify (e.g. a title with only a
// player's last name shouldn't be excluded just because the search also
// included a first name) - it only needs to match at least one. Listings
// matching more words are ranked higher via relevance sorting instead of
// being excluded outright. This restores how the MVP/beta search behaved.
// Merges the listing index with the backend keyword-search supplement (see
// fetchKeywordMatches in SearchPage.duck.js), de-duplicated by id. Listings
// present in both keep the index's copy for freshness, but if the backend
// copy was trusted as a match (fromBackendKeywordSearch - see
// getManualTokenMatchScore below), that trust carries over rather than
// being silently dropped. Without this, a listing that only matches via its
// description (which the index never scans, only title/publicData - see
// fetchListingIndex) would flash into the results as soon as the backend
// supplement resolves, then vanish again once the index finishes loading
// and its untrusted copy of the same listing replaces the trusted one,
// scoring 0 on the next local-only match pass.
const unionListingsById = (primary = [], extra = []) => {
  const byId = new Map();
  primary.forEach(l => byId.set(`${l?.id?.uuid || l?.id}`, l));
  extra.forEach(l => {
    const key = `${l?.id?.uuid || l?.id}`;
    const existing = byId.get(key);
    if (!existing) {
      byId.set(key, l);
    } else if (l?.fromBackendKeywordSearch && !existing.fromBackendKeywordSearch) {
      byId.set(key, { ...existing, fromBackendKeywordSearch: true });
    }
  });
  return Array.from(byId.values());
};

const countMatchingWords = (textValues, words) => {
  if (!words || words.length === 0) return 0;
  if (!textValues || textValues.length === 0) return 0;
  // Fold accents on the listing side too (words are already folded by the
  // caller) - so typing "Suarez" still finds a title stored as "Suárez" and
  // vice versa.
  const normalizedValues = textValues
    .filter(value => typeof value === 'string' && value.trim().length > 0)
    .map(value => foldToBaseLetters(value));
  return words.reduce(
    (count, word) => count + (normalizedValues.some(value => value.includes(word)) ? 1 : 0),
    0
  );
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      selectedTab: 'all',
      filtersExpanded: false,
      searchTerm: null, // Store the text search term from header
    };

    // Persist filter options across narrowing searches so multi-selects don't lose choices
    this.persistedOptions = {
      club: [],
      league: [],
      player: [],
      country: [],
      season: [],
    };

    // Track per-filter selections for shared 'keywords' param
    this.keywordSelections = {
      club: [],
      league: [],
      player: [],
      country: [],
      season: [],
    };
    this.manualKeywordTokens = [];
    this._lastKeywords = undefined;
    this._lastOptionsSignature = undefined;
    this._lastTrackedSearch = undefined;

    // Tracks which listing IDs were last requested via onFetchListingsByIds,
    // so repeated renders that land on the same page (same filters/sort/page)
    // don't re-request them. Set from render(), read in componentDidMount /
    // componentDidUpdate - see maybeFetchPageListings().
    this._pendingPageIds = null;
    this._pendingPageIdsKey = null;
    this._lastFetchedPageIdsKey = null;

    // Tracks the free-text query last sent to onFetchKeywordMatches (the
    // backend keywords-search supplement to the listing index - see
    // maybeFetchKeywordMatches()), so it's only re-requested when the typed
    // search text actually changes.
    this._pendingKeywordMatchQuery = null;
    this._lastFetchedKeywordMatchQuery = null;

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
  }

  trackSearchIfChanged(keywords) {
    const trimmed = String(keywords ?? '').trim();
    if (!trimmed || trimmed === this._lastTrackedSearch) {
      return;
    }
    this._lastTrackedSearch = trimmed;
    trackMetaEvent('Search', {
      customData: { search_string: trimmed },
      ...getUserIdentifiers(this.props.currentUser),
    });
  }

  // Client requirement: a Search event should also fire when a league/club/size filter is
  // applied, not just the free-text search box (trackSearchIfChanged above). Hooked here
  // rather than in each individual filter widget since every filter change - dropdown
  // selection, badge removal, etc. - funnels through getHandleChangedValueFn with the
  // changed param(s) in updatedURLParams. Only fires on an actual new value, not on a
  // filter being cleared/removed (removing a filter isn't "applying a search").
  trackFilterSearchIfRelevant(updatedURLParams) {
    if (!updatedURLParams) return;

    ['league', 'club', 'size'].forEach(key => {
      const paramName = constructQueryParamName(key, 'public');
      if (!Object.prototype.hasOwnProperty.call(updatedURLParams, paramName)) {
        return;
      }
      const rawValue = updatedURLParams[paramName];
      if (!rawValue) {
        return;
      }
      const searchString = Array.isArray(rawValue)
        ? rawValue.join(', ')
        : String(rawValue).replace(/^has_(all|any):/, '');
      trackMetaEvent('Search', {
        customData: { search_string: searchString },
        ...getUserIdentifiers(this.props.currentUser),
      });
    });
  }

  componentDidMount() {
    // Initialize selected tab based on for sale option in URL (from header scope selection)
    this.syncTabFromQueryParams();

    // Fetch the (cached, lightweight) listing index on mount so the Club/
    // League/Player/Land/Saison filter options are available immediately.
    // This is a single small-payload scan (no images), reused - not
    // refetched - for every filter/keyword/sort/tab combination afterwards.
    const { config, onFetchListingIndex, location } = this.props;
    if (onFetchListingIndex) {
      onFetchListingIndex(config);
    }

    // Track a direct/shared search-URL landing (e.g. ?keywords=... on load)
    const initialKeywords = (parse(location.search) || {}).keywords;
    this.trackSearchIfChanged(initialKeywords);

    this.maybeFetchPageListings();
    this.maybeFetchKeywordMatches();
  }

  componentDidUpdate(prevProps) {
    // If URL search params change, keep selectedTab in sync with pub_for_sale_option
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTabFromQueryParams();
      this.trackSearchIfChanged((parse(this.props.location.search) || {}).keywords);
    }

    // Whenever a render has computed a new set of page-listing IDs to show
    // (filters/sort/page/tab changed, or the listing index just finished
    // loading), fetch full data for exactly those IDs.
    this.maybeFetchPageListings();
    this.maybeFetchKeywordMatches();
  }

  // Requests full listing data (images/author) for the page of IDs that the
  // most recent render computed (see the `shouldFilterListings` branch in
  // render()), but only if that ID set actually changed - render() runs on
  // every prop/state change, so this is what keeps it from re-requesting the
  // same page repeatedly.
  maybeFetchPageListings() {
    const key = this._pendingPageIdsKey;
    if (key && key !== this._lastFetchedPageIdsKey) {
      this._lastFetchedPageIdsKey = key;
      const { config, onFetchListingsByIds } = this.props;
      onFetchListingsByIds(this._pendingPageIds, config);
    }
  }

  // Requests the backend keyword-search supplement (see fetchKeywordMatches
  // in SearchPage.duck.js) for the free-text query the most recent render
  // computed, but only if that query actually changed - this is what keeps
  // it from re-requesting on every unrelated re-render (page/sort/tab
  // changes, index finishing its own load, etc.).
  maybeFetchKeywordMatches() {
    const query = this._pendingKeywordMatchQuery || '';
    if (query !== this._lastFetchedKeywordMatchQuery) {
      this._lastFetchedKeywordMatchQuery = query;
      const { config, onFetchKeywordMatches } = this.props;
      if (onFetchKeywordMatches) {
        onFetchKeywordMatches(query, config);
      }
    }
  }

  syncTabFromQueryParams() {
    const { location } = this.props;
    const params = parse(location.search) || {};
    const scope = params.scope; // preferred explicit scope from URL
    const forSale = params.pub_for_sale_option; // backwards compatibility
    let tab = 'all';
    if (scope === 'users') tab = 'users';
    else if (scope === 'forSale') tab = 'for_sale';
    else if (scope === 'notForSale') tab = 'not_for_sale';
    else if (scope === 'all' || !scope) {
      // No explicit scope; infer from for_sale_option
      if (forSale === 'for_sale') tab = 'for_sale';
      else if (forSale === 'not_for_sale') tab = 'not_for_sale';
      else tab = 'all';
    }

    if (this.state.selectedTab !== tab) {
      this.setState({ selectedTab: tab });
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll() {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Clear persisted options so fresh unfiltered results can repopulate them
    this.persistedOptions = { club: [], league: [], player: [], country: [], season: [] };
    // Clear keyword selections too
    this.keywordSelections = { club: [], league: [], player: [], country: [], season: [] };
    this.manualKeywordTokens = [];
    this._lastKeywords = undefined;
    this._lastOptionsSignature = undefined;

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params - remove all filter params including keywords
    // BUT keep the tab selection for for_sale_option (e.g. pub_for_sale_option)
    let forSaleParamName = 'pub_for_sale_option';
    try {
      const forSaleCfg = Array.isArray(listingFieldsConfig)
        ? listingFieldsConfig.find(f => f?.key === 'for_sale_option')
        : null;
      if (forSaleCfg) {
        // constructQueryParamName is imported and used elsewhere in this file
        forSaleParamName = constructQueryParamName(forSaleCfg.key, forSaleCfg.scope);
      }
    } catch (e) {
      // fall back to default
    }

    const paramsToKeep = {
      [forSaleParamName]: urlQueryParams[forSaleParamName],
      scope: urlQueryParams.scope,
    };

    const queryParams = paramsToKeep;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    // Use push to navigate to clean search page
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Drop 'shuffle' (see the "Entdecken" nav link) as soon as any filter
    // changes - it isn't dropped by validUrlQueryParamsFromProps' whitelist
    // (called with dropNonFilterParams=false), so left alone it would keep
    // silently overriding whatever sort/filter order the user picks next
    // (render() prefers shuffle order over sortConfig - see shuffleSeed
    // below).
    const urlQueryParams = omit(validUrlQueryParamsFromProps(this.props), 'shuffle');

    return updatedURLParams => {
      const remappedParams = updatedURLParams;
      this.trackFilterSearchIfRelevant(remappedParams);

      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        // prevState.currentQueryParams can still carry a stale 'shuffle' (e.g.
        // seeded into state on initial mount from the landing URL - see the
        // constructor), so it has to be stripped again here, not just off
        // urlQueryParams above.
        const mergedQueryParams = omit(
          { ...urlQueryParams, ...prevState.currentQueryParams },
          'shuffle'
        );

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // For keywords: if main search type is keywords AND this update does not explicitly
        // change 'keywords', we keep the existing keywords from URL. Otherwise we let the
        // updated value win (e.g. filters like League set keywords to league_id).
        const isKeywordSearch = isMainSearchTypeKeywords(config);
        const isUpdatingKeywords = Object.prototype.hasOwnProperty.call(remappedParams || {}, 'keywords');
        const keywordsMaybe = isKeywordSearch && !isUpdatingKeywords ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, remappedParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...keywordsMaybe,
              ...remappedParams,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          // Use replace/push with location state to indicate to the Routes
          // scroll handler that this navigation should not trigger a page scroll.
          const navState = { skipScroll: true };
          // Use history.replace for live updates to URL so the page does not jump.
          // replace updates the current history entry and is less likely to trigger
          // scroll behavior compared to push.
          const targetPath = createResourceLocatorString(routeName, routeConfiguration, pathParams, search);
          if (history && typeof history.replace === 'function') {
            history.replace(targetPath, navState);
          } else {
            history.push(targetPath, navState);
          }
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    // Drop 'shuffle' (see the "Entdecken" nav link) as soon as the user picks
    // an explicit sort - otherwise it silently keeps overriding whatever sort
    // they choose (render() prefers shuffle order over sortConfig - see
    // shuffleSeed below) and "Sort by price"/etc. would look like it does
    // nothing.
    const urlQueryParams = omit(validUrlQueryParamsFromProps(this.props), 'shuffle');

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  handleTabSelect(tabKey) {
    this.setState({ selectedTab: tabKey });
    if (tabKey === 'users') {
      // Fetch all authors when users tab is selected
      const { config, onFetchAuthors } = this.props;
      // onFetchAuthors(config);
      // Also write explicit scope so Topbar can sync its search scope selector
      this.getHandleChangedValueFn(true)({ pub_for_sale_option: undefined, scope: 'users' });
    } else if (tabKey === 'all') {
      this.getHandleChangedValueFn(true)({ pub_for_sale_option: undefined, scope: 'all' });
    } else if (tabKey === 'for_sale') {
      this.getHandleChangedValueFn(true)({ pub_for_sale_option: 'for_sale', scope: 'forSale' });
    } else if (tabKey === 'not_for_sale') {
      this.getHandleChangedValueFn(true)({ pub_for_sale_option: 'not_for_sale', scope: 'notForSale' });
    }
  }


  // Merge handler for the shared 'keywords' param used by 5 filters
  handleKeywordsFilterChange = (filterKey, formattedParamObj) => {
    const val = formattedParamObj ? formattedParamObj.keywords : null;

    this.keywordSelections[filterKey] = val ? parseSelectFilterOptions(val) : [];
    const keywordsValue = combineKeywordTokens(this.manualKeywordTokens, this.keywordSelections);

    // Preserve the current scope parameter when updating keywords
    const { location } = this.props;
    const currentParams = parse(location.search) || {};
    const scopeMaybe = currentParams.scope ? { scope: currentParams.scope } : {};

    this.getHandleChangedValueFn(true)({ keywords: keywordsValue, ...scopeMaybe });
  };

  // Remove a specific selection from a keyword-backed filter
  handleRemoveFilterBadge = (filterKey, valueToRemove) => {
    this.keywordSelections[filterKey] = (this.keywordSelections[filterKey] || []).filter(
      v => `${v}` !== `${valueToRemove}`
    );

    const keywordsValue = combineKeywordTokens(this.manualKeywordTokens, this.keywordSelections);

    // Preserve the current scope parameter when updating keywords
    const { location } = this.props;
    const currentParams = parse(location.search) || {};
    const scopeMaybe = currentParams.scope ? { scope: currentParams.scope } : {};

    this.getHandleChangedValueFn(true)({ keywords: keywordsValue, ...scopeMaybe });
  };

  handleClearSearchTerm = () => {
    const { history, routeConfiguration, location, config } = this.props;

    const keywordMode = isMainSearchTypeKeywords(config);
    if (keywordMode) {
      this.manualKeywordTokens = [];
      const keywordsValue = combineKeywordTokens(this.manualKeywordTokens, this.keywordSelections);
      this._lastKeywords = keywordsValue || '';
      this._lastOptionsSignature = undefined;

      // Preserve the current scope parameter when clearing keywords
      const currentParams = parse(location.search) || {};
      const scopeMaybe = currentParams.scope ? { scope: currentParams.scope } : {};

      this.getHandleChangedValueFn(true)({ keywords: keywordsValue, ...scopeMaybe });
      return;
    }

    const params = parse(location.search, { latlng: ['origin'], latlngBounds: ['bounds'] }) || {};

    delete params.address;
    delete params.bounds;
    delete params.origin;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    const stringified = Object.keys(params).length > 0 ? params : {};
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, stringified));
  };


  render() {
    const isUsersTab = this.state.selectedTab === 'users';
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
      listingIndex,
      listingIndexLoading,
      listingIndexLoaded,
      pageListings,
      pageListingsLoading,
      keywordMatchListings,
      keywordMatchQuery,
      keywordMatchLoading,
      keywordMatchLoaded,
    } = this.props;
    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const rawParams = parse(location.search) || {};

    const validQueryParams = {
      ...urlQueryParams,
      verified: rawParams.verified,
    };
    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
        f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
      )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Exclude for_sale_option from filter UI (handled elsewhere via tabs)
    const availableFiltersToRender = availableFilters.filter(f => f.key !== 'for_sale_option');

    // Client request: Größe (size) should show as the 3rd filter in the
    // mobile filter panel specifically. That panel's Club/Liga/Spieler/Land/
    // Saison/Verification block is its own fixed sequence of individually-
    // written components, not driven by this array's order at all - so
    // reordering this shared array alone (tried first) only reshuffled Größe
    // among the *other* generically rendered filters there (Preis/Zustand/
    // Mannschaft), never reaching position 3.
    //
    // sizeFilterConfig is looked up here (NOT removed from
    // availableFiltersToRender - that array is also used for the filter
    // badges row and the desktop filter bar, both of which still need Größe
    // in their own normal place) and rendered explicitly, once, in that fixed
    // mobile sequence instead - the mobile panel's own trailing map over
    // availableFiltersToRender excludes 'size' specifically so it doesn't
    // also render there a second time.
    const sizeFilterConfig = availableFiltersToRender.find(f => f.key === 'size');

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null || searchParamsInURL.dates === selectedFilters.dates;
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      Number(pagination?.totalItems) ||
      (pagination?.paginationUnsupported
        ? listings.length
        : 0);

    // Club/League/Player/Land/Saison, the Verified filter, and manually typed
    // keywords are all resolved against the listing index rather than the
    // backend page - only fold the index/page-listings fetches into the
    // loading state when one of those is actually in play, so plain
    // unfiltered browsing never waits on the (background) catalog scan.
    const hasClientFilterHint = !!(validQueryParams?.keywords || validQueryParams?.verified);
    // A typed search with no Club/League/Player/Land/Saison dropdown selection
    // active doesn't need the full listing index at all (see the
    // needsFullIndex/listingsToFilter split below) - it's answered entirely by
    // the fast native backend keyword-match supplement - so it shouldn't wait
    // on the slow index fetch either. this.keywordSelections is already
    // populated by the time render() runs (the filter UI's own onChange
    // handlers update it directly), so it's safe to read here even though the
    // equivalent anyIntersectionFilterActive flag isn't computed until later
    // in this function.
    const needsFullIndexHint =
      Object.values(this.keywordSelections || {}).some(
        arr => Array.isArray(arr) && arr.length > 0
      ) || !!validQueryParams?.verified;
    // !listingIndexLoaded matters on top of listingIndexLoading: right after
    // a hard page refresh (or the very first render), the index fetch
    // hasn't been dispatched yet at all, so listingIndexLoading is still
    // false too - without this, a filtered search would render against an
    // empty index for a moment and flash "no results found" before
    // correcting itself once the fetch actually starts. Same reasoning
    // applies to the backend keyword-match supplement (keywordMatchLoaded) -
    // otherwise a search for something only an older, index-cap-excluded
    // listing matches would flash "no results" before that fetch resolves.
    const isLoading =
      searchInProgress ||
      (hasClientFilterHint &&
        ((needsFullIndexHint && (!listingIndexLoaded || listingIndexLoading)) ||
          pageListingsLoading ||
          !keywordMatchLoaded ||
          keywordMatchLoading));

    const listingsAreLoaded =
      !isLoading &&
      searchParamsAreInSync &&
      !!(
        hasPaginationInfo ||
        pagination?.paginationUnsupported ||
        listingIndex.length > 0 ||
        // A pure typed search (no dropdown/Verified filter) never needed the
        // index in the first place (see needsFullIndexHint) - once the
        // keyword-match supplement itself has loaded, that's a real answer
        // even while listingIndex is still empty/still loading in the
        // background, not a sign anything is stuck.
        (!needsFullIndexHint && keywordMatchLoaded)
      );

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);

    // Use filter options from Redux state (fetched from all listings)
    // This ensures all filter options are available regardless of current page
    const { filterOptions: fetchedFilterOptions } = this.props;
    // Fallback to building from current listings if filter options haven't loaded yet
    const toSortedOptionsFromMap = map =>
      Array.from(map.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ option: id, label: name }));

    let clubOptionsFromCurrentPage = [];
    let leagueOptionsFromCurrentPage = [];
    let playerOptionsFromCurrentPage = [];
    let countryOptionsFromCurrentPage = [];
    let seasonOptionsFromCurrentPage = [];

    if (!fetchedFilterOptions) {
      // Fallback: build from current page listings while filter options are loading
      const clubMap = new Map();
      const leagueMap = new Map();
      const playerMap = new Map();
      const countryMap = new Map();
      const seasonMap = new Map();
      (listings || []).forEach(l => {
        const pd = l?.attributes?.publicData || {};
        if (pd.club_id && pd.club && !clubMap.has(pd.club_id)) clubMap.set(pd.club_id, pd.club);
        if (pd.league_id && pd.league && !leagueMap.has(pd.league_id)) leagueMap.set(pd.league_id, pd.league);
        if (pd.player_id && pd.player && !playerMap.has(pd.player_id)) playerMap.set(pd.player_id, pd.player);
        if (pd.country_id && (pd.country || pd.country_id) && !countryMap.has(pd.country_id)) {
          countryMap.set(pd.country_id, pd.country || pd.country_id);
        }
        if (pd.season_id && (pd.season || pd.season_id) && !seasonMap.has(pd.season_id)) {
          seasonMap.set(pd.season_id, pd.season || pd.season_id);
        }
      });

      clubOptionsFromCurrentPage = toSortedOptionsFromMap(clubMap);
      leagueOptionsFromCurrentPage = toSortedOptionsFromMap(leagueMap);
      playerOptionsFromCurrentPage = toSortedOptionsFromMap(playerMap);
      countryOptionsFromCurrentPage = toSortedOptionsFromMap(countryMap);
      seasonOptionsFromCurrentPage = toSortedOptionsFromMap(seasonMap);
    }

    // Use fetched options if available, otherwise fall back to current page options
    const clubOptionsNow = fetchedFilterOptions?.club || clubOptionsFromCurrentPage;
    const leagueOptionsNow = fetchedFilterOptions?.league || leagueOptionsFromCurrentPage;
    const playerOptionsNow = fetchedFilterOptions?.player || playerOptionsFromCurrentPage;
    const countryOptionsNow = fetchedFilterOptions?.country || countryOptionsFromCurrentPage;
    const seasonOptionsNow = fetchedFilterOptions?.season || seasonOptionsFromCurrentPage;

    // Merge helper that unions by 'option' and returns sorted by label
    const mergeUniqueSorted = (prev = [], next = [], filterType = '') => {
      const map = new Map();

      [...prev, ...next].forEach(o => {
        if (!map.has(o.option)) {
          map.set(o.option, o.label);
        }
      });

      let items = Array.from(map.entries()).map(([option, label]) => ({
        option,
        label,
      }));

      // Filter out unwanted names only for player filter (test, demo, placeholder, etc.) in German and English
      if (filterType === 'player') {
        items = filterValidOptions(items, 'all');
        // Player names shouldn't contain numbers or symbols (jersey numbers,
        // placeholders like "#16", "(10)", "..." typed into the free-text
        // field instead of a real name) - letters, spaces, hyphens, dots,
        // apostrophes and umlauts only.
        items = items.filter(o => looksLikeARealName(o.label));
      }

      // Filter season options by valid format (YY/MM)
      if (filterType === 'season') {
        items = filterValidSeasons(items);
      }

      // Collapse near-duplicate spellings (missing umlauts/diacritics) of the
      // same club or player into a single entry.
      if (filterType === 'player' || filterType === 'club') {
        items = dedupeNearDuplicateLabels(items);
      }

      // Put Nationalmannschaft first only for club filter
      if (filterType === 'club') {
        items = items.sort((a, b) => {
          if (a.label === 'Nationalmannschaft') return -1;
          if (b.label === 'Nationalmannschaft') return 1;
          return a.label.localeCompare(b.label);
        });
      }
      else if (filterType === 'player') {
        items = items.sort((a, b) => {
          if (a.label === 'Kein Flock') return -1;
          if (b.label === 'Kein Flock') return 1;
          return a.label.localeCompare(b.label);
        });
      } else {
        items = items.sort((a, b) => a.label.localeCompare(b.label));
      }

      return items;
    };

    // Persist and expose merged options so choices don't disappear after a selection
    this.persistedOptions.club = mergeUniqueSorted(
      this.persistedOptions.club,
      clubOptionsNow,
      'club'
    );
    this.persistedOptions.league = mergeUniqueSorted(this.persistedOptions.league, leagueOptionsNow, '');
    this.persistedOptions.player = mergeUniqueSorted(this.persistedOptions.player, playerOptionsNow, 'player');
    this.persistedOptions.country = mergeUniqueSorted(this.persistedOptions.country, countryOptionsNow, '');
    this.persistedOptions.season = mergeUniqueSorted(this.persistedOptions.season, seasonOptionsNow, 'season');

    // Show full persisted options regardless of current selections
    const clubOptions = this.persistedOptions.club;
    const leagueOptions = this.persistedOptions.league;
    const playerOptions = this.persistedOptions.player;
    const countryOptions = this.persistedOptions.country;
    const seasonOptions = this.persistedOptions.season;
    // Intersection logic across keyword-backed filters: OR within a filter, AND across filters
    const keywordsRaw = (validQueryParams?.keywords ?? searchParamsInURL?.keywords ?? '') || '';
    const keywordTokens = keywordsRaw ? parseSelectFilterOptions(keywordsRaw) : [];
    const optionLists = [clubOptions, leagueOptions, playerOptions, countryOptions, seasonOptions];

    // Build a signature for the option lists so we can detect when they change
    const optionsSignature = optionLists
      .map(list => (list || []).map(o => `${o.option}`).join(','))
      .join('|');

    // Recalculate keyword selections if keywords changed OR if options changed
    if (this._lastKeywords !== keywordsRaw || this._lastOptionsSignature !== optionsSignature) {
      const deriveTokensFor = optionsList => {
        if (!optionsList || optionsList.length === 0 || keywordTokens.length === 0) return [];
        // Id-only match (see extractManualKeywordTokens for why label
        // matching was removed) - a typed word must never be silently
        // reinterpreted as picking a dropdown option just because it
        // happens to equal that option's display label.
        const idSet = new Set(optionsList.map(o => `${o.option}`));
        return keywordTokens.map(t => `${t}`).filter(t => idSet.has(t));
      };
      this.keywordSelections = {
        club: deriveTokensFor(clubOptions),
        league: deriveTokensFor(leagueOptions),
        player: deriveTokensFor(playerOptions),
        country: deriveTokensFor(countryOptions),
        season: deriveTokensFor(seasonOptions),
      };
      this.manualKeywordTokens = extractManualKeywordTokens(keywordTokens, optionLists);
      this._lastKeywords = keywordsRaw;
      this._lastOptionsSignature = optionsSignature;
    }

    // Stash the free-text query for this render - read by
    // maybeFetchKeywordMatches() in componentDidMount/componentDidUpdate to
    // fetch the backend keyword-search supplement (see SearchPage.duck.js)
    // for exactly this text.
    this._pendingKeywordMatchQuery = (this.manualKeywordTokens || []).join(' ');

    const buildSelectedIdSet = (optionsList, tokensArr) => {
      if (!optionsList || optionsList.length === 0 || !tokensArr || tokensArr.length === 0)
        return new Set();
      // Id-only match, same reasoning as deriveTokensFor above - tokensArr
      // is already just the ids that deriveTokensFor recognized, so this is
      // mostly a sanity re-check against the current optionsList.
      const valuesSet = new Set(optionsList.map(o => `${o.option}`));
      const ids = new Set();
      tokensArr.forEach(tok => {
        const t = `${tok}`;
        if (valuesSet.has(t)) ids.add(t);
      });
      return ids;
    };

    // Use per-filter selections (ids or labels) rather than the entire keywords token list
    const selectedClubIds = buildSelectedIdSet(clubOptions, this.keywordSelections.club);
    const selectedLeagueIds = buildSelectedIdSet(leagueOptions, this.keywordSelections.league);
    const selectedPlayerIds = buildSelectedIdSet(playerOptions, this.keywordSelections.player);
    const selectedCountryIds = buildSelectedIdSet(countryOptions, this.keywordSelections.country);
    const selectedSeasonIds = buildSelectedIdSet(seasonOptions, this.keywordSelections.season);

    let searchTermDisplay;
    if (isKeywordSearch) {
      const textSearchTokens = this.manualKeywordTokens;
      searchTermDisplay = textSearchTokens.length > 0 ? textSearchTokens.join(' ') : null;
    } else {
      searchTermDisplay = searchParamsInURL?.address || null;
    }

    const anyIntersectionFilterActive =
      selectedClubIds.size > 0 ||
      selectedLeagueIds.size > 0 ||
      selectedPlayerIds.size > 0 ||
      selectedCountryIds.size > 0 ||
      selectedSeasonIds.size > 0;

    const manualTokensLower = (this.manualKeywordTokens || []).map(token => foldToBaseLetters(`${token}`));
    // Break each comma-separated token into its individual words, so a
    // typed phrase like "Luis Suarez" is matched word-by-word instead of as
    // one literal two-word substring (see splitIntoWords above).
    const manualSearchWords = manualTokensLower.flatMap(splitIntoWords);
    const manualTokensActive = manualSearchWords.length > 0;

    // Populated as a side effect of listingMatchesManualTokens below (called
    // once per listing during the filter pass) so relevance sorting further
    // down doesn't need to redo the text scan.
    const matchScoreById = new Map();

    // Big enough to always outrank a non-title match regardless of word
    // count: a listing titled "... Völler ..." should always beat one that
    // only mentions Völler in some other field (player, club, season, or -
    // for a backend keyword match - the description), never lose to it just
    // because that other listing happens to be newer (the tie-break once
    // both land on the same score). Multiplying by the number of matched
    // title words (not just a flat bonus) still lets "matches both words in
    // the title" outrank "matches only one", same as the non-title tier
    // already did.
    const TITLE_MATCH_WEIGHT = 1000;

    // A typed full year (e.g. "1999") never appears literally in a "Saison"
    // value like "93/99" or "99/00" - sellers only store the two-digit
    // suffix - so it would never match via the generic word scan below.
    // Client-requested behaviour: searching a full year should surface
    // listings whose season includes that year's two-digit suffix, ranked
    // ABOVE every other match (title included), hence a weight well above
    // TITLE_MATCH_WEIGHT rather than folding into the normal word count.
    const SEASON_YEAR_MATCH_WEIGHT = TITLE_MATCH_WEIGHT * 10;
    const searchedSeasonYearSuffixes = manualSearchWords
      .map(word => yearToSeasonSuffix(word))
      .filter(Boolean);

    const getManualTokenMatchScore = listing => {
      const attrs = listing?.attributes || {};
      const title = attrs.title;
      const publicData = attrs.publicData || {};
      const titleValues = typeof title === 'string' ? [title] : [];
      const otherValues = [];
      Object.values(publicData).forEach(value => {
        if (typeof value === 'string') {
          otherValues.push(value);
        } else if (Array.isArray(value)) {
          value.forEach(item => {
            if (typeof item === 'string') {
              otherValues.push(item);
            }
          });
        }
      });
      const titleMatchCount = countMatchingWords(titleValues, manualSearchWords);
      const otherMatchCount = countMatchingWords(otherValues, manualSearchWords);
      // Some listings only carry season_id (not a separate season label) -
      // same fallback fetchListingIndex uses when building the season
      // filter options (see seasonLabel there).
      const seasonValue = publicData.season || publicData.season_id;
      const seasonYearMatchBonus =
        searchedSeasonYearSuffixes.length > 0 &&
        searchedSeasonYearSuffixes.some(suffix => seasonIncludesYearSuffix(seasonValue, suffix))
          ? SEASON_YEAR_MATCH_WEIGHT
          : 0;
      const localScore =
        titleMatchCount * TITLE_MATCH_WEIGHT + otherMatchCount + seasonYearMatchBonus;
      if (localScore > 0) return localScore;
      // This scan only sees title + publicData - a listing merged in from
      // the backend keyword-search supplement (see fetchKeywordMatches) may
      // have matched on its description instead, which never reaches here.
      // Sharetribe's own full-text search already vetted it as relevant to
      // this exact query (see the keywordMatchQuery check where
      // listingsToFilter is built), so trust that rather than scoring it 0
      // and dropping it right back out. Treated as a non-title-tier match
      // (same as a publicData-only hit) since which field Sharetribe's own
      // search actually matched on isn't visible here.
      return listing?.fromBackendKeywordSearch ? manualSearchWords.length : 0;
    };

    // A listing qualifies for a manually typed search as soon as it matches
    // AT LEAST ONE of the search words (OR, not AND) - e.g. searching "Luis
    // Suarez" must still surface a listing titled just "... Suarez". Listings
    // matching more words are ranked higher via matchScoreById + the
    // relevance sort below, rather than the rest being excluded outright.
    const listingMatchesManualTokens = listing => {
      if (!manualTokensActive) return true;
      const score = getManualTokenMatchScore(listing);
      const key = listing?.id?.uuid || listing?.id;
      if (key != null) matchScoreById.set(key, score);
      return score > 0;
    };

    // Determine if there are any badge-producing filters active (excluding the For Sale tab)
    // Badges are shown for:
    // 1) Keyword-backed intersection filters (club/league/player/country/season)
    // 2) Enum-based listing-field filters rendered from availableFiltersToRender
    const hasEnumBadges = availableFiltersToRender.some(filterConfig => {
      const enumOptions = filterConfig.enumOptions;
      if (!Array.isArray(enumOptions)) return false;
      const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope);
      const raw = selectedFilters[paramName];
      if (!raw) return false;
      const tokens = Array.isArray(raw) ? raw : parseSelectFilterOptions(raw);
      return tokens.length > 0;
    });

    const hasVerifiedFilter = !!selectedFilters?.verified;

    const hasFilterBadges =
      anyIntersectionFilterActive ||
      manualTokensActive ||
      hasEnumBadges ||
      hasVerifiedFilter;

    // Whether any client-side narrowing needs to run at all: either a dropdown-backed
    // filter (club/league/player/country/season) or a manually typed keyword that didn't
    // match one of those options.
    // The "Verified Users" filter is applied purely client-side (see
    // filteredByVerification below), so - like the club/league/player/country/
    // season filters - it needs the full result set rather than just the
    // current backend page to report an accurate count and paginate correctly.
    const shouldFilterListings = anyIntersectionFilterActive || manualTokensActive || hasVerifiedFilter;

    // Club/League/Player/Land/Saison/Verified/free-text matching runs against
    // the lightweight listing index (newest listings, capped - see
    // MAX_INDEX_LISTINGS in fetchListingIndex) only when one of those is
    // actually active; otherwise the backend page (already
    // filtered/paginated server-side) is used as-is. The index is empty
    // until it finishes loading, which the isLoading/hasClientFilterHint
    // check above already covers with a loading state.
    //
    // When a manual free-text search is active, the index is also unioned
    // with the backend keyword-search supplement (see fetchKeywordMatches)
    // so listings older than the index cap are still reachable - but only
    // when keywordMatchQuery actually matches the query just computed for
    // this render (this._pendingKeywordMatchQuery, set just above). Without
    // that check, a leftover result set from whatever the *previous* search
    // text was would still be sitting in props for one render after the
    // text changes (the fetch for the new text hasn't resolved yet), and -
    // since a listing merged in this way is trusted as an match outright,
    // see getManualTokenMatchScore - it would flash in as a false match for
    // an unrelated new query instead of just being left out until the
    // up-to-date supplement arrives.
    const keywordMatchListingsForQuery =
      manualTokensActive && keywordMatchQuery === this._pendingKeywordMatchQuery
        ? keywordMatchListings
        : [];
    // A Club/League/Player/Land/Saison dropdown selection (or the Verified
    // filter) needs the full scanned index - only it carries the club_id/
    // league_id/etc a dropdown filters by. A plain typed search with none of
    // those active doesn't: it's answered entirely by the native backend
    // keyword-match supplement above, so it skips the (capped, slower to
    // build) index entirely rather than unioning it in for no benefit - see
    // the matching needsFullIndexHint split in the isLoading computation
    // above, which is what keeps this case from waiting on the index either.
    const needsFullIndex = anyIntersectionFilterActive || hasVerifiedFilter;
    const listingsToFilter = shouldFilterListings
      ? needsFullIndex
        ? manualTokensActive
          ? unionListingsById(listingIndex, keywordMatchListingsForQuery)
          : listingIndex
        : keywordMatchListingsForQuery
      : listings;

    // Filter by free-text intersection filters (club, league, player, country, season)
    // and/or manually typed keyword tokens.
    // The listing index covers every tab (for_sale / not_for_sale) at once
    // (see fetchListingIndex), so the currently selected tab has to be
    // enforced here - the backend query that would normally do this isn't
    // what's producing these results.
    const selectedForSaleOption = selectedFilters?.pub_for_sale_option;

    const filteredByIntersection = shouldFilterListings
      ? (listingsToFilter || []).filter(l => {
        const pd = l?.attributes?.publicData || {};
        const matchSet = (set, key) => set.size === 0 || (pd?.[key] != null && set.has(`${pd[key]}`));
        return (
          (!selectedForSaleOption || pd?.for_sale_option === selectedForSaleOption) &&
          matchSet(selectedClubIds, 'club_id') &&
          matchSet(selectedLeagueIds, 'league_id') &&
          matchSet(selectedPlayerIds, 'player_id') &&
          matchSet(selectedCountryIds, 'country_id') &&
          matchSet(selectedSeasonIds, 'season_id') &&
          listingMatchesManualTokens(l)
        );
      })
      : listingsToFilter;

    // Apply client-side filters (enum filters, price filter, etc.) - the
    // listing index isn't backend-filtered by any of these, so they always
    // need to be re-applied here whenever shouldFilterListings is active.
    const applyClientSideFilters = (listings) => {
      if (!shouldFilterListings) {
        // Not using the listing index, so the backend already filtered by
        // enums, price and tab.
        return listings;
      }

      // Apply each filter from selectedFilters
      // The selectedFilters has keys like 'pub_condition', 'pub_price', etc.
      // We need to match them using constructQueryParamName
      return listings.filter(listing => {
        const pd = listing?.attributes?.publicData || {};

        // Check each filter in selectedFilters
        for (const [filterKey, filterValue] of Object.entries(selectedFilters)) {
          if (!filterValue) {
            continue;
          }

          // Handle price filter separately
          if (filterKey === 'pub_price' || filterKey === 'price') {
            // Price filter format: "min,max" in major units (e.g., "10,50")
            const priceValues = filterValue.split(',');
            if (priceValues.length === 2) {
              const minPrice = parseFloat(priceValues[0]);
              const maxPrice = parseFloat(priceValues[1]);
              const listingPrice = listing.attributes?.price?.amount || 0;

              // Convert listing price from subunits to major units (cents to dollars)
              const listingPriceMajor = listingPrice / 100;

              // Check if listing price is within range
              if (listingPriceMajor < minPrice || listingPriceMajor > maxPrice) {
                return false;
              }
            }
            continue;
          }

          // Find the matching filter config by constructing its query param name
          const filterConfig = availableFiltersToRender.find(f => {
            const paramName = constructQueryParamName(f.key, f.scope);
            return paramName === filterKey;
          });

          if (!filterConfig) {
            continue;
          }
          if (filterConfig.schemaType !== 'enum' && filterConfig.schemaType !== 'multi-enum') {
            continue;
          }

          // Parse the filter value
          const selectedOptions = Array.isArray(filterValue)
            ? filterValue
            : parseSelectFilterOptions(filterValue);

          if (selectedOptions.length === 0) continue;

          // Get the listing's value for this field
          const fieldKey = filterConfig.key;
          const listingValue = pd[fieldKey];

          if (filterConfig.schemaType === 'enum') {
            // Single enum: listing value must be one of the selected options
            if (!selectedOptions.includes(listingValue)) {
              return false;
            }
          } else if (filterConfig.schemaType === 'multi-enum') {
            // Multi-enum: listing value is an array, must have at least one match
            const listingValues = Array.isArray(listingValue) ? listingValue : [];
            const hasMatch = selectedOptions.some(opt => listingValues.includes(opt));
            if (!hasMatch) {
              return false;
            }
          }
        }

        return true; // Listing matches all filters
      });
    };

    const filteredListings = applyClientSideFilters(filteredByIntersection);
    // ✅ VERIFIED FILTER
    const selectedVerified = validQueryParams?.verified;

    const filteredByVerification = filteredListings.filter(listing => {
      if (selectedVerified !== "verified_users") return true;

      // Index entries carry `verified` directly; plain backend listing
      // objects (the shouldFilterListings === false path, which never
      // reaches here with this filter active) carry it on the author.
      const isVerified =
        listing?.verified ??
        listing?.author?.attributes?.profile?.metadata?.verified_by_marketplace;

      return !!isVerified;
    });

    // BLOCKED USERS: hide listings authored by anyone the current viewer has blocked
    // (see BlockUser.js on ProfilePage / server/api/block-user.js) - "remove it from the
    // user's feed instantly" per Apple App Review Guideline 1.2. Blocked ids live in the
    // viewer's own currentUser.privateData (never exposed to anyone else), computed
    // unconditionally here so it applies on both the plain backend-page path and the
    // client-filtered listing-index path above.
    const blockedUserIds = currentUser?.attributes?.profile?.privateData?.blockedUserIds || [];
    const filteredByBlockedUsers =
      blockedUserIds.length === 0
        ? filteredByVerification
        : filteredByVerification.filter(listing => {
            const authorId = listing?.author?.id?.uuid || listing?.author?.id;
            return !blockedUserIds.includes(authorId);
          });

    const urlParams = parse(location.search);
    // A truthy but otherwise unused value (e.g. a timestamp) that only seeds
    // a deterministic order below - never compared or dereferenced further.
    const shuffleSeed = urlParams.shuffle;

    let sortedFilteredListings = filteredByBlockedUsers;

    if (shuffleSeed) {
      // Deterministic "shuffle": listings are ordered by a stable hash of
      // (seed + listing id) instead of Math.random(). A random comparator
      // here previously reordered the array differently on every single
      // render - which fed straight into the _pendingPageIdsKey change
      // detection below (the page's listing ids differed every render),
      // triggering a runaway fetch -> re-render -> reshuffle -> re-fetch
      // loop that pegged the main thread and froze the page (worse the
      // moment a filter was touched, since selecting one causes its own
      // extra re-renders on top). Hashing keeps the order identical across
      // renders for the same seed, while still changing whenever a fresh
      // seed is used (e.g. a new Date.now() from a "shuffle" nav link).
      const hashOf = str => {
        let h = 0;
        for (let i = 0; i < str.length; i++) {
          h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
        }
        return h;
      };
      sortedFilteredListings = [...filteredByVerification].sort((a, b) => {
        const idA = `${a?.id?.uuid || a?.id}`;
        const idB = `${b?.id?.uuid || b?.id}`;
        return hashOf(`${shuffleSeed}:${idA}`) - hashOf(`${shuffleSeed}:${idB}`);
      });
    } else if (shouldFilterListings) {
      // The listing index is fetched once and shared across every sort/tab/
      // filter combination (see fetchListingIndex), so - unlike the plain
      // backend page, which the Marketplace API already sorts - the chosen
      // sort has to be applied here.
      const sortParam = validQueryParams?.[sortConfig.queryParamName];
      const isPriceSort = sortParam === 'price' || sortParam === '-price';
      const isCreatedAtSort = sortParam === 'createdAt' || sortParam === '-createdAt';
      // While a manual keyword search is active, always rank by how well
      // each listing matched (title matches weighted far above the rest -
      // see TITLE_MATCH_WEIGHT/matchScoreById above), newest first as a
      // tie-breaker - this is what makes e.g. "Rudi Völler" show a listing
      // actually titled that first, instead of one that only happens to
      // mention it in some other field, or a newer unrelated listing.
      //
      // This used to only apply when no sort was chosen at all (or
      // 'relevance' explicitly) - but every nav link (Discover, the tab
      // links, etc.) sets sort=createdAt in its URL by default, so in
      // practice a typed search almost always arrived with that sort param
      // already set, meaning relevance ranking rarely actually engaged.
      // Client's ask was for title matches to always take priority while
      // searching, so a manual keyword search now overrides whatever sort
      // is selected - a real title match should outrank a newer/pricier
      // unrelated listing, not the other way around.
      const isRelevanceSort = manualTokensActive;

      if (isRelevanceSort) {
        const scoreOf = listing => matchScoreById.get(listing?.id?.uuid || listing?.id) ?? 0;
        sortedFilteredListings = [...filteredByVerification].sort((a, b) => {
          const scoreDiff = scoreOf(b) - scoreOf(a);
          if (scoreDiff !== 0) return scoreDiff;
          return (
            new Date(b.attributes?.createdAt || 0).getTime() -
            new Date(a.attributes?.createdAt || 0).getTime()
          );
        });
      } else {
        // Match the Marketplace API's own sort semantics (see the options
        // list in configSearch.js): 'price' = highest first, '-price' =
        // lowest first; 'createdAt' = newest first, '-createdAt' = oldest
        // first. Previously this always sorted newest-first for anything
        // that wasn't a price sort (so "Oldest" did nothing here) and
        // treated '-price' as highest-first (the opposite of its
        // "lowest price" label) whenever a keyword/filter search was active.
        const desc = isPriceSort
          ? sortParam === 'price'
          : isCreatedAtSort
          ? sortParam === 'createdAt'
          : true;
        const getValue = listing =>
          isPriceSort
            ? listing.attributes?.price?.amount ?? 0
            : new Date(listing.attributes?.createdAt || 0).getTime();
        sortedFilteredListings = [...filteredByVerification].sort((a, b) => {
          const diff = getValue(a) - getValue(b);
          return desc ? -diff : diff;
        });
      }
    }

    // Read page parameter directly from URL since validQueryParams doesn't include it
    const currentPage = parseInt(urlParams.page) || 1;
    const itemsPerPage = Number(pagination?.perPage) || RESULT_PAGE_SIZE;

    let listingsForPanel;
    let resultsCount;
    let paginationForPanel;
    // Whether the hydrated (images/author) data for the current filtered
    // page has actually arrived yet - used below to keep showing the
    // loading state instead of flashing an empty/incomplete grid while
    // fetchListingsByIds for a *new* page of IDs is still in flight.
    let filteredPageDataPending = false;

    if (shouldFilterListings) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      const pageEntries = sortedFilteredListings.slice(startIndex, endIndex);
      const pageIds = pageEntries.map(entry => entry.id);
      const pageIdKeys = pageIds.map(id => id?.uuid || id);

      // Stash the page of IDs the current render landed on - read by
      // maybeFetchPageListings() in componentDidMount/componentDidUpdate to
      // fetch full (image/author) data for exactly this page.
      this._pendingPageIds = pageIds;
      this._pendingPageIdsKey = pageIdKeys.join(',');

      const pageListingsById = {};
      (pageListings || []).forEach(l => {
        pageListingsById[l.id?.uuid || l.id] = l;
      });
      listingsForPanel = pageIdKeys.map(key => pageListingsById[key]).filter(Boolean);
      filteredPageDataPending = pageIdKeys.length > 0 && listingsForPanel.length !== pageIdKeys.length;

      resultsCount = sortedFilteredListings.length;

      paginationForPanel = {
        page: currentPage,
        perPage: itemsPerPage,
        totalItems: sortedFilteredListings.length,
        totalPages: Math.max(1, Math.ceil(sortedFilteredListings.length / itemsPerPage)),
      };
    } else {
      this._pendingPageIds = null;
      this._pendingPageIdsKey = null;

      listingsForPanel = sortedFilteredListings;
      resultsCount = pagination?.totalItems ?? sortedFilteredListings.length;

      paginationForPanel = {
        ...pagination,
        totalItems: resultsCount,
        // Recompute totalPages from resultsCount too - leaving the backend's
        // own totalPages in place here was the exact bug behind "0 results
        // but pagination shows 77 pages": totalItems got overridden above
        // but totalPages didn't, so the two went out of sync.
        totalPages: Math.max(1, Math.ceil(resultsCount / itemsPerPage)),
      };
    }

    // Folds in the "hydrated data for the current filtered page hasn't
    // arrived yet" case, on top of the earlier isLoading/listingsAreLoaded,
    // which are computed before that's known - see filteredPageDataPending.
    const isResultsPending = isLoading || filteredPageDataPending;
    const listingsAreLoadedFinal = listingsAreLoaded && !filteredPageDataPending;

    const getInit = qp => initialValues(this.props, this.state.currentQueryParams)([qp], true);

      const sortBy = mode => {
        return sortConfig.active ? (
          <SortBy
            sort={validQueryParams[sortConfig.queryParamName]}
            isConflictingFilterActive={!!conflictingFilterActive}
            hasConflictingFilters={sortConfig.conflictingFilters?.length > 0}
            selectedFilters={selectedFilters}
            onSelect={this.handleSortBy}
            showAsPopup
            mode={mode}
            contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
          />
        ) : null;
      };
      const noResultsInfo = (
        <NoSearchResultsMaybe
          listingsAreLoaded={listingsAreLoadedFinal}
          totalItems={resultsCount}
          location={location}
          resetAll={this.resetAll}
          showCreateListingsLink={showCreateListingsLink}
        />
      );

      const { title, description, schema } = createSearchResultSchema(
        listings,
        searchParamsInURL || {},
        intl,
        routeConfiguration,
        config
      );

      // Set topbar class based on if a modal is open in
      // a child component
      const topbarClasses = this.state.isMobileModalOpen
        ? classNames(css.topbarBehindModal, css.topbar)
        : css.topbar;

      // N.B. openMobileMap button is sticky.
      // For some reason, stickyness doesn't work on Safari, if the element is <button>

      const hydratedListingsForPanel = listingsForPanel;

      return (
        <Page
          scrollingDisabled={scrollingDisabled}
          description={description}
          title={title}
          schema={schema}
        >
          <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />

          {/* Display search term with clear button - placed ABOVE tabs */}
          {searchTermDisplay && (
            <div
              className={css.searchTermDisplay}
            >
              <span className={css.searchTermLabel}>
                Suchergebnisse: <strong>{searchTermDisplay}</strong>
              </span>
              <button
                type="button"
                className={css.searchTermClear}
                onClick={this.handleClearSearchTerm}
                aria-label="Clear search"
              >
                <CloseIcon width={24} height={24} />
              </button>
            </div>
          )}

          <SearchPageTabs selected={this.state.selectedTab} onSelect={this.handleTabSelect} />

          <div className={css.layoutWrapperContainer}>
            {!isUsersTab && (
              <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
                <div
                  className={classNames(css.filterColumnContent, {
                    [css.filterColumnContentExpanded]: this.state.filtersExpanded,
                    [css.filterColumnContentCollapsed]: !this.state.filtersExpanded,
                  })}
                >
                  {/* Dynamic Club filter (multi-select by name) */}
                  {this.props.filterOptionsLoading && clubOptions.length === 0 ? (
                    <div className={css.filterLoadingPlaceholder}>
                      <IconSpinner />
                    </div>
                  ) : clubOptions.length > 0 ? (
                    <SelectMultipleFilter
                      id="SearchFiltersDesktop.club"
                      name="club"
                      label={intl.formatMessage({ id: 'FilterComponent.clubLabel', defaultMessage: 'Club' })}
                      queryParamNames={['keywords']}
                      initialValues={getInit('keywords')}
                      onSubmit={values => this.handleKeywordsFilterChange('club', values)}
                      options={clubOptions}
                      className={css.filter}
                      showAsPopup
                      isDesktop
                    />
                  ) : null}

                  {/* Dynamic League filter (multi-select by name) */}
                  {this.props.filterOptionsLoading && leagueOptions.length === 0 ? (
                    <div className={css.filterLoadingPlaceholder}>
                      <IconSpinner />
                    </div>
                  ) : leagueOptions.length > 0 ? (
                    <SelectMultipleFilter
                      id="SearchFiltersDesktop.league"
                      name="league"
                      label={intl.formatMessage({ id: 'FilterComponent.leagueLabel', defaultMessage: 'League' })}
                      queryParamNames={['keywords']}
                      initialValues={getInit('keywords')}
                      onSubmit={values => this.handleKeywordsFilterChange('league', values)}
                      options={leagueOptions}
                      className={css.filter}
                      showAsPopup
                      isDesktop
                    />
                  ) : null}

                  {/* Dynamic Player filter (multi-select by name) */}
                  {this.props.filterOptionsLoading && playerOptions.length === 0 ? (
                    <div className={css.filterLoadingPlaceholder}>
                      <IconSpinner />
                    </div>
                  ) : playerOptions.length > 0 ? (
                    <SelectMultipleFilter
                      id="SearchFiltersDesktop.player"
                      name="player"
                      label={intl.formatMessage({ id: 'FilterComponent.playerLabel', defaultMessage: 'Player' })}
                      queryParamNames={['keywords']}
                      initialValues={getInit('keywords')}
                      onSubmit={values => this.handleKeywordsFilterChange('player', values)}
                      options={playerOptions}
                      className={css.filter}
                      showAsPopup
                      isDesktop
                    />
                  ) : null}

                  {/* Dynamic Country filter (multi-select by name) */}
                  {this.props.filterOptionsLoading && countryOptions.length === 0 ? (
                    <div className={css.filterLoadingPlaceholder}>
                      <IconSpinner />
                    </div>
                  ) : countryOptions.length > 0 ? (
                    <SelectMultipleFilter
                      id="SearchFiltersDesktop.country"
                      name="country"
                      label={intl.formatMessage({ id: 'FilterComponent.countryLabel', defaultMessage: 'Country' })}
                      queryParamNames={['keywords']}
                      initialValues={getInit('keywords')}
                      onSubmit={values => this.handleKeywordsFilterChange('country', values)}
                      options={countryOptions}
                      className={css.filter}
                      showAsPopup
                      isDesktop
                    />
                  ) : null}

                  {/* Dynamic Season filter (multi-select by name) */}

                  {this.props.filterOptionsLoading && seasonOptions.length === 0 ? (
                    <div className={css.filterLoadingPlaceholder}>
                      <IconSpinner />
                    </div>
                  ) : seasonOptions.length > 0 ? (
                    <SelectMultipleFilter
                      id="SearchFiltersDesktop.season"
                      name="season"
                      label={intl.formatMessage({ id: 'FilterComponent.seasonLabel', defaultMessage: 'Season' })}
                      queryParamNames={['keywords']}
                      initialValues={getInit('keywords')}
                      onSubmit={values => this.handleKeywordsFilterChange('season', values)}
                      options={seasonOptions}
                      className={css.filter}
                      showAsPopup
                      isDesktop
                    />
                  ) : null}

                  <SelectMultipleFilter
                    id="SearchFiltersDesktop.verified"
                    name="verified"
                    label="Verification"
                    queryParamNames={['verified']}
                    initialValues={{
                      verified: validQueryParams?.verified || null
                    }}
                    onSubmit={values =>
                      this.getHandleChangedValueFn(true)({ verified: values?.verified })
                    }
                    options={[
                      { option: 'verified_users', label: 'Verified Users' },
                      // { option: 'all_users', label: 'All Users' },
                    ]}
                    className={css.filter}
                    showAsPopup
                    isDesktop
                    isUserFilter
                  />

                  {availableFiltersToRender
                    .filter(filterConfig => {
                      if (
                        this.state.selectedTab === 'not_for_sale' &&
                        filterConfig.key === 'price'
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map(filterConfig => {
                      const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key
                        }`;
                      return (
                        <FilterComponent
                          key={key}
                          idPrefix="SearchFiltersDesktop"
                          className={css.filter}
                          config={filterConfig}
                          listingCategories={listingCategories}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          liveEdit
                          showAsPopup
                          isDesktop
                        />
                      );
                    })}
                </div>
                <button
                  type="button"
                  className={css.filtersToggle}
                  onClick={() =>
                    this.setState(s => ({ filtersExpanded: !s.filtersExpanded }))
                  }
                >
                  <AdjustmentsIcon className={css.filtersToggleIcon} width={24} height={24} />
                  {this.state.filtersExpanded ? 'Weniger Filter' : 'Mehr Filter'}
                </button>

                {/* Active filter badges */}
                {hasFilterBadges && (
                  <div className={css.filterBadgesContainer}>
                    <div className={css.filterBadges}>
                      {/* Club badges */}
                      {selectedClubIds.size > 0 &&
                        Array.from(selectedClubIds).map(clubId => {
                          const option = clubOptions.find(o => `${o.option}` === `${clubId}`);
                          return option ? (
                            <FilterBadge
                              key={`club-${clubId}`}
                              label={option.label}
                              onRemove={() => this.handleRemoveFilterBadge('club', clubId)}
                            />
                          ) : null;
                        })}

                      {/* League badges */}
                      {selectedLeagueIds.size > 0 &&
                        Array.from(selectedLeagueIds).map(leagueId => {
                          const option = leagueOptions.find(o => `${o.option}` === `${leagueId}`);
                          return option ? (
                            <FilterBadge
                              key={`league-${leagueId}`}
                              label={option.label}
                              onRemove={() => this.handleRemoveFilterBadge('league', leagueId)}
                            />
                          ) : null;
                        })}

                      {/* Player badges */}
                      {selectedPlayerIds.size > 0 &&
                        Array.from(selectedPlayerIds).map(playerId => {
                          const option = playerOptions.find(o => `${o.option}` === `${playerId}`);
                          return option ? (
                            <FilterBadge
                              key={`player-${playerId}`}
                              label={option.label}
                              onRemove={() => this.handleRemoveFilterBadge('player', playerId)}
                            />
                          ) : null;
                        })}

                      {/* Country badges */}
                      {selectedCountryIds.size > 0 &&
                        Array.from(selectedCountryIds).map(countryId => {
                          const option = countryOptions.find(o => `${o.option}` === `${countryId}`);
                          return option ? (
                            <FilterBadge
                              key={`country-${countryId}`}
                              label={option.label}
                              onRemove={() => this.handleRemoveFilterBadge('country', countryId)}
                            />
                          ) : null;
                        })}

                      {/* Season badges */}
                      {selectedSeasonIds.size > 0 &&
                        Array.from(selectedSeasonIds).map(seasonId => {
                          const option = seasonOptions.find(o => `${o.option}` === `${seasonId}`);
                          return option ? (
                            <FilterBadge
                              key={`season-${seasonId}`}
                              label={option.label}
                              onRemove={() => this.handleRemoveFilterBadge('season', seasonId)}
                            />
                          ) : null;
                        })}

                      {/* Verified badge */}
                      {selectedFilters?.verified && selectedFilters.verified === 'verified_users' && (
                        <FilterBadge
                          key="verified-users"
                          label="Verified Users"
                          onRemove={() => {
                            const currentParams = parse(this.props.location.search) || {};
                            const scopeMaybe = currentParams.scope ? { scope: currentParams.scope } : {};

                            this.getHandleChangedValueFn(true)({
                              verified: null, // remove filter
                              ...scopeMaybe,
                            });
                          }}
                        />
                      )}

                      {/* Other filters badges (size, condition, etc) */}
                      {availableFiltersToRender.map(filterConfig => {
                        // Only handle listing-field enum filters here
                        const enumOptions = filterConfig.enumOptions;
                        if (!Array.isArray(enumOptions)) return null;
                        const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope);
                        const raw = selectedFilters[paramName];
                        if (!raw) return null;

                        const startsWithAll = typeof raw === 'string' && raw.indexOf('has_all:') === 0;
                        const startsWithAny = typeof raw === 'string' && raw.indexOf('has_any:') === 0;
                        const tokens = Array.isArray(raw) ? raw : parseSelectFilterOptions(raw);

                        const toLabel = value => {
                          const opt = enumOptions.find(o => o.option === value || o.key === value);
                          return opt?.label || value;
                        };

                        const rebuildRaw = leftTokens => {
                          if (!leftTokens || leftTokens.length === 0) return null;
                          const joined = leftTokens.join(',');
                          if (startsWithAll) return `has_all:${joined}`;
                          if (startsWithAny) return `has_any:${joined}`;
                          return joined;
                        };

                        return tokens.map(token => (
                          <FilterBadge
                            key={`${paramName}-${token}`}
                            label={toLabel(token)}
                            onRemove={() => {
                              const newTokens = tokens.filter(t => `${t}` !== `${token}`);
                              const newRaw = rebuildRaw(newTokens);

                              // Preserve the current scope parameter
                              const currentParams = parse(this.props.location.search) || {};
                              const scopeMaybe = currentParams.scope ? { scope: currentParams.scope } : {};

                              this.getHandleChangedValueFn(true)({ [paramName]: newRaw, ...scopeMaybe });
                            }}
                          />
                        ));
                      })}
                    </div>

                    <button
                      type="button"
                      className={css.filterBadgesReset}
                      onClick={this.resetAll}
                    >
                      <ArrowUturnLeftIcon width={24} height={24} />
                      <FormattedMessage id="SearchPage.resetAllFilters" />
                    </button>
                  </div>
                )}
              </aside>
            )}

            <div className={css.layoutWrapperMain} role="main">
              <div className={css.searchResultContainer}>
                {!isUsersTab && (
                  <SearchFiltersMobile
                    className={css.searchFiltersMobileList}
                    urlQueryParams={validQueryParams}
                    sortByComponent={sortBy('mobile')}
                    listingsAreLoaded={listingsAreLoadedFinal}
                    resultsCount={resultsCount}
                    searchInProgress={isResultsPending}
                    searchListingsError={searchListingsError}
                    showAsModalMaxWidth={MODAL_BREAKPOINT}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onOpenModal={this.onOpenMobileModal}
                    onCloseModal={this.onCloseMobileModal}
                    resetAll={this.resetAll}
                    selectedFiltersCount={selectedFiltersCountForMobile}
                    isMapVariant={false}
                    noResultsInfo={noResultsInfo}
                    location={location}
                  >
                    {/* Dynamic Club filter (multi-select by name) - mobile */}
                    {this.props.filterOptionsLoading && clubOptions.length === 0 ? (
                      <div className={css.filterLoadingPlaceholder}>
                        <IconSpinner />
                      </div>
                    ) : clubOptions.length > 0 ? (
                      <SelectMultipleFilterMobile
                        id="SearchFiltersMobile.club"
                        name="club"
                        label={intl.formatMessage({ id: 'FilterComponent.clubLabel', defaultMessage: 'Club' })}
                        queryParamNames={['keywords']}
                        initialValues={getInit('keywords')}
                        onSubmit={values => this.handleKeywordsFilterChange('club', values)}
                        options={clubOptions}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                      />
                    ) : null}

                    {/* Dynamic League filter (multi-select by name) - mobile */}
                    {this.props.filterOptionsLoading && leagueOptions.length === 0 ? (
                      <div className={css.filterLoadingPlaceholder}>
                        <IconSpinner />
                      </div>
                    ) : leagueOptions.length > 0 ? (
                      <SelectMultipleFilterMobile
                        id="SearchFiltersMobile.league"
                        name="league"
                        label={intl.formatMessage({ id: 'FilterComponent.leagueLabel', defaultMessage: 'League' })}
                        queryParamNames={['keywords']}
                        initialValues={getInit('keywords')}
                        onSubmit={values => this.handleKeywordsFilterChange('league', values)}
                        options={leagueOptions}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                      />
                    ) : null}

                    {/* Größe (size) - client request: show as the 3rd filter, right after
                        Club/Liga - pulled out of availableFiltersToRender's own generic
                        map (see above) and rendered here explicitly instead. */}
                    {sizeFilterConfig && (
                      <FilterComponentMobile
                        key={`SearchFiltersDesktop.${sizeFilterConfig.scope || 'built-in'}.${sizeFilterConfig.key}`}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={sizeFilterConfig}
                        selectedTab={this.state.selectedTab}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                        liveEdit
                      />
                    )}

                    {/* Dynamic Player filter (multi-select by name) - mobile */}
                    {this.props.filterOptionsLoading && playerOptions.length === 0 ? (
                      <div className={css.filterLoadingPlaceholder}>
                        <IconSpinner />
                      </div>
                    ) : playerOptions.length > 0 ? (
                      <SelectMultipleFilterMobile
                        id="SearchFiltersMobile.player"
                        name="player"
                        label={intl.formatMessage({ id: 'FilterComponent.playerLabel', defaultMessage: 'Player' })}
                        queryParamNames={['keywords']}
                        initialValues={getInit('keywords')}
                        onSubmit={values => this.handleKeywordsFilterChange('player', values)}
                        options={playerOptions}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                      />
                    ) : null}

                    {/* Dynamic Country filter (multi-select by name) - mobile */}
                    {this.props.filterOptionsLoading && countryOptions.length === 0 ? (
                      <div className={css.filterLoadingPlaceholder}>
                        <IconSpinner />
                      </div>
                    ) : countryOptions.length > 0 ? (
                      <SelectMultipleFilterMobile
                        id="SearchFiltersMobile.country"
                        name="country"
                        label={intl.formatMessage({ id: 'FilterComponent.countryLabel', defaultMessage: 'Country' })}
                        queryParamNames={['keywords']}
                        initialValues={getInit('keywords')}
                        onSubmit={values => this.handleKeywordsFilterChange('country', values)}
                        options={countryOptions}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                      />
                    ) : null}

                    {/* Dynamic Season filter (multi-select by name) - mobile */}
                    {this.props.filterOptionsLoading && seasonOptions.length === 0 ? (
                      <div className={css.filterLoadingPlaceholder}>
                        <IconSpinner />
                      </div>
                    ) : seasonOptions.length > 0 ? (
                      <SelectMultipleFilterMobile
                        id="SearchFiltersMobile.season"
                        name="season"
                        label={intl.formatMessage({ id: 'FilterComponent.seasonLabel', defaultMessage: 'Season' })}
                        queryParamNames={['keywords']}
                        initialValues={getInit('keywords')}
                        onSubmit={values => this.handleKeywordsFilterChange('season', values)}
                        options={seasonOptions}
                        showAsModalMaxWidth={MODAL_BREAKPOINT}
                        onManageDisableScrolling={onManageDisableScrolling}
                        resultsCount={resultsCount}
                      />
                    ) : null}

                    <SelectMultipleFilterMobile
                      id="SearchFiltersMobile.verified"
                      name="verified"
                      label="Verification"
                      queryParamNames={['verified']}
                      initialValues={{
                        verified: validQueryParams?.verified
                          ? [validQueryParams.verified]
                          : []
                      }}
                      onSubmit={values =>
                        this.getHandleChangedValueFn(true)({ verified: values?.verified })
                      }
                      options={[
                        { option: 'verified_users', label: 'Verified Users' },
                        // { option: 'all_users', label: 'All Users' },
                      ]}
                      showAsModalMaxWidth={MODAL_BREAKPOINT}
                      onManageDisableScrolling={onManageDisableScrolling}
                      resultsCount={resultsCount}
                    // isSingleSelect={true}
                    />

                    {availableFiltersToRender
                      .filter(filterConfig => {
                        if (
                          this.state.selectedTab === 'not_for_sale' &&
                          filterConfig.key === 'price'
                        ) {
                          return false;
                        }
                        // Größe (size) is rendered explicitly above, right
                        // after Liga, instead of wherever it'd otherwise fall
                        // in this generic list - skip it here so it doesn't
                        // render a second time.
                        if (filterConfig.key === 'size') {
                          return false;
                        }
                        return true;
                      })
                      .map(filterConfig => {
                        const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key
                          }`;
                        return (
                          <FilterComponentMobile
                            key={key}
                            idPrefix="SearchFiltersDesktop"
                            className={css.filter}
                            config={filterConfig}
                            selectedTab={this.state.selectedTab}
                            listingCategories={listingCategories}
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            showAsModalMaxWidth={MODAL_BREAKPOINT}
                            onManageDisableScrolling={onManageDisableScrolling}
                            resultsCount={resultsCount}
                            liveEdit
                          />
                        );
                      })}
                  </SearchFiltersMobile>
                )}
                {!isUsersTab && (
                  <MainPanelHeader
                    className={css.mainPanel}
                    sortByComponent={sortBy('desktop')}
                    isSortByActive={sortConfig.active}
                    listingsAreLoaded={listingsAreLoadedFinal}
                    resultsCount={resultsCount}
                    searchInProgress={isResultsPending}
                    searchListingsError={searchListingsError}
                    noResultsInfo={noResultsInfo}
                  />
                )}

                <div
                  id={`tabpanel-${this.state.selectedTab}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${this.state.selectedTab}`}

                >
                  {isUsersTab ? (
                    <UsersPanel
                      authors={this.props.authors}
                      loading={this.props.authorsLoading}
                      error={this.props.authorsError}
                      progress={this.props.authorsProgress}
                    />
                  ) : (
                    <>
                      {searchListingsError ? (
                        <H3 className={css.error}>
                          <FormattedMessage id="SearchPage.searchError" />
                        </H3>
                      ) : null}
                      {!isValidDatesFilter ? (
                        <H5>
                          <FormattedMessage id="SearchPage.invalidDatesFilter" />
                        </H5>
                      ) : null}
                      {isResultsPending ? (
                        <div className={css.centerLoader}>
                          <IconSpinner />
                        </div>
                      ) : (
                        <SearchResultsPanel
                          className={css.searchListingsPanel}
                          listings={listingsForPanel}
                          pagination={paginationForPanel}
                          search={parse(location.search)}
                          isMapVariant={false}
                          listingTypeParam={listingTypePathParam}
                          key={shouldFilterListings ? 'filtered-listings' : 'normal-listings'}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </Page>
      );
    }
  }

  /**
   * SearchPage component with grid layout (no map)
   *
   * @param {Object} props
   * @param {propTypes.currentUser} [props.currentUser] - The current user
   * @param {Array<propTypes.listing>} [props.listings] - The listings
   * @param {propTypes.pagination} [props.pagination] - The pagination
   * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
   * @param {boolean} [props.searchInProgress] - Whether the search is in progress
   * @param {propTypes.error} [props.searchListingsError] - The search listings error
   * @param {Object} [props.searchParams] - The search params from the Redux state
   * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
   * @returns {JSX.Element}
   */
  const EnhancedSearchPage = props => {
    const config = useConfiguration();
    const routeConfiguration = useRouteConfiguration();
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();

    const searchListingsError = props.searchListingsError;
    if (isForbiddenError(searchListingsError)) {
      // This can happen if private marketplace mode is active
      return (
        <NamedRedirect
          name="SignupPage"
          state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        />
      );
    }

    const { currentUser, ...restOfProps } = props;
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
    const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
    const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
    const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

    if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
      return (
        <NamedRedirect
          name="NoAccessPage"
          params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
        />
      );
    } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
      return (
        <NamedRedirect
          name="NoAccessPage"
          params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
        />
      );
    }

    return (
      <SearchPageComponent
        config={config}
        routeConfiguration={routeConfiguration}
        intl={intl}
        history={history}
        location={location}
        currentUser={currentUser}
        {...restOfProps}
      />
    );
  };

  const mapStateToProps = state => {
    const { currentUser } = state.user;
    const {
      pagination,
      searchInProgress,
      searchListingsError,
      searchParams,
      authors,
      authorsLoading,
      authorsError,
      authorsProgress,
      filterOptions,
      listingIndex,
      listingIndexLoading,
      listingIndexLoaded,
      listingIndexError,
      pageListings,
      pageListingsLoading,
      pageListingsError,
      keywordMatchListings,
      keywordMatchQuery,
      keywordMatchLoading,
      keywordMatchLoaded,
    } = state.SearchPage;
    const listings = state.SearchPage.currentPageResultIds || [];

    return {
      currentUser,
      listings,
      pagination,
      scrollingDisabled: isScrollingDisabled(state),
      searchInProgress,
      searchListingsError,
      searchParams,
      authors,
      authorsLoading,
      authorsError,
      authorsProgress,
      filterOptions,
      // Exposed under the same prop names the filter dropdowns already read
      // (e.g. this.props.filterOptionsLoading) - only the underlying Redux
      // state key changed (see fetchListingIndex in SearchPage.duck.js).
      filterOptionsLoading: listingIndexLoading,
      filterOptionsError: listingIndexError,
      listingIndex,
      listingIndexLoading,
      listingIndexLoaded,
      pageListings,
      pageListingsLoading,
      pageListingsError,
      keywordMatchListings,
      keywordMatchQuery,
      keywordMatchLoading,
      keywordMatchLoaded,
    };
  };

  const mapDispatchToProps = dispatch => ({
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onFetchAuthors: (config) => dispatch(fetchAuthors(config)),
    onFetchListingIndex: (config) => dispatch(fetchListingIndex(config)),
    onFetchListingsByIds: (ids, config) => dispatch(fetchListingsByIds(ids, config)),
    onFetchKeywordMatches: (searchText, config) => dispatch(fetchKeywordMatches(searchText, config)),
  });

  // Note: it is important that the withRouter HOC is **outside** the
  // connect HOC, otherwise React Router won't rerender any Route
  // components since connect implements a shouldComponentUpdate
  // lifecycle hook.
  //
  // See: https://github.com/ReactTraining/react-router/issues/4671
  const SearchPage = compose(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )
  )(EnhancedSearchPage);

export default SearchPage;
